import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart, Line, BarChart, Bar, ComposedChart, Area, AreaChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { healthData, healthRiskZones, diseaseAlerts } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import {
  Thermometer, Droplets, Activity, AlertTriangle, Heart, Zap,
  Wind, Sun, Building2, Clock, MapPin, CheckCircle, ChevronDown,
  ChevronUp, Shield, TrendingUp, TrendingDown, Minus, Brain,
  Stethoscope, Pill, FlaskConical, Users
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const HEALTH_TREND_EXTENDED = [
  { time:"05:00", heatstroke:1,  dehydration:4,  injury:2,  serious:0, referral:0 },
  { time:"06:00", heatstroke:2,  dehydration:8,  injury:4,  serious:1, referral:0 },
  { time:"07:00", heatstroke:6,  dehydration:18, injury:8,  serious:2, referral:1 },
  { time:"08:00", heatstroke:11, dehydration:31, injury:14, serious:4, referral:1 },
  { time:"09:00", heatstroke:18, dehydration:49, injury:22, serious:6, referral:2 },
  { time:"10:00", heatstroke:23, dehydration:62, injury:29, serious:8, referral:3 },
  { time:"11:00", heatstroke:28, dehydration:74, injury:34, serious:9, referral:4 },
];

const FORECAST_CASES = [
  { time:"12:00", p25:26, p50:31, p75:38 },
  { time:"13:00", p25:28, p50:34, p75:42 },
  { time:"14:00", p25:32, p50:40, p75:50 },
  { time:"15:00", p25:38, p50:48, p75:60 },
  { time:"16:00", p25:42, p50:54, p75:68 },
  { time:"17:00", p25:36, p50:46, p75:58 },
  { time:"18:00", p25:24, p50:31, p75:40 },
];

const ENVIRONMENTAL = [
  { zone:"Ramkund Ghat",    temp:41.2, heatIndex:52, humidity:48, uv:10, aqi:118, shade:"8%",  misting:"Active"  },
  { zone:"Panchvati",       temp:40.4, heatIndex:49, humidity:52, uv:9,  aqi:104, shade:"12%", misting:"Active"  },
  { zone:"Trimbakeshwar",   temp:38.8, heatIndex:46, humidity:56, uv:8,  aqi:88,  shade:"18%", misting:"Standby" },
  { zone:"Nashik Road Stn", temp:39.6, heatIndex:47, humidity:50, uv:9,  aqi:96,  shade:"22%", misting:"Active"  },
  { zone:"Kalaram Temple",  temp:38.4, heatIndex:45, humidity:54, uv:8,  aqi:82,  shade:"28%", misting:"Off"     },
  { zone:"Gangapur Camp",   temp:37.2, heatIndex:43, humidity:58, uv:7,  aqi:74,  shade:"35%", misting:"Off"     },
];

const CAMP_DETAIL = [
  {
    id:"Alpha", name:"Camp Alpha — Ramkund",  zone:"Ramkund Ghat",    beds:50, load:62, status:"over",
    doctors:3, paramedics:8, nurses:6,
    cases:{ heatstroke:18, dehydration:26, injury:12, chronic:6 },
    supplies:{ ors:420, iv:180, oxygen:8, stretchers:12, paracetamol:840 },
    refToday:2, nearHospital:"Civil Hospital 2.8km", lastInspect:"13:45",
  },
  {
    id:"Beta",  name:"Camp Beta — Panchvati",  zone:"Panchvati",       beds:50, load:41, status:"normal",
    doctors:2, paramedics:6, nurses:4,
    cases:{ heatstroke:8, dehydration:18, injury:9, chronic:6 },
    supplies:{ ors:640, iv:240, oxygen:6, stretchers:10, paracetamol:1200 },
    refToday:1, nearHospital:"Wockhardt 4.1km", lastInspect:"14:10",
  },
  {
    id:"Gamma", name:"Camp Gamma — Trimbak",   zone:"Trimbakeshwar",   beds:50, load:47, status:"near",
    doctors:2, paramedics:5, nurses:4,
    cases:{ heatstroke:11, dehydration:22, injury:8, chronic:6 },
    supplies:{ ors:580, iv:200, oxygen:5, stretchers:8, paracetamol:1060 },
    refToday:1, nearHospital:"Trimbak PHC 1.2km", lastInspect:"14:02",
  },
  {
    id:"Delta", name:"Camp Delta — Nashik Rd", zone:"Nashik Road",     beds:40, load:28, status:"normal",
    doctors:1, paramedics:4, nurses:3,
    cases:{ heatstroke:4, dehydration:12, injury:6, chronic:6 },
    supplies:{ ors:820, iv:300, oxygen:4, stretchers:6, paracetamol:1500 },
    refToday:0, nearHospital:"Civil Hospital 1.4km", lastInspect:"13:58",
  },
];

const HOSPITALS = [
  { name:"Civil Hospital Nashik",  beds:420, occupied:284, icu:24, icuUsed:18, distance:"2.8 km", status:"Accepting",  speciality:"General + Trauma" },
  { name:"Wockhardt Hospital",     beds:280, occupied:196, icu:16, icuUsed:10, distance:"4.1 km", status:"Accepting",  speciality:"Cardio + ICU"    },
  { name:"Zen Multi Specialty",    beds:180, occupied:104, icu:10, icuUsed:4,  distance:"5.2 km", status:"Accepting",  speciality:"General"          },
  { name:"GMCH Nashik (Govt.)",    beds:640, occupied:380, icu:40, icuUsed:28, distance:"3.6 km", status:"Near Full",  speciality:"All departments"  },
  { name:"Trimbak PHC",            beds:30,  occupied:22,  icu:0,  icuUsed:0,  distance:"1.2 km", status:"Overflow",   speciality:"Primary care"     },
];

const ORS_ZONES = [
  { zone:"Ramkund Ghat",    points:28, distributed:14200, stock:8400,  pct:63, status:"Low"     },
  { zone:"Panchvati",       points:18, distributed:9800,  stock:12600, pct:84, status:"OK"      },
  { zone:"Trimbakeshwar",   points:14, distributed:7400,  stock:10200, pct:73, status:"OK"      },
  { zone:"Nashik Road Stn", points:12, distributed:5600,  stock:14800, pct:88, status:"Good"    },
  { zone:"Kalaram Temple",  points:10, distributed:4200,  stock:9400,  pct:91, status:"Good"    },
  { zone:"Gangapur Camp",   points:8,  distributed:2800,  stock:11600, pct:94, status:"Good"    },
];

const SUPPLY_STOCK = [
  { item:"ORS Packets",         total:67000, used:44000, pct:66, unit:"pkts",   alert:false },
  { item:"IV Fluids (Saline)",  total:2200,  used:920,   pct:42, unit:"bags",   alert:false },
  { item:"Paracetamol 500mg",   total:12000, used:4600,  pct:38, unit:"strips", alert:false },
  { item:"Oral Rehydration Salt",total:8800, used:4200,  pct:48, unit:"sachets",alert:false },
  { item:"Antihistamine Tabs",  total:3200,  used:1840,  pct:58, unit:"strips", alert:false },
  { item:"Oxygen Cylinders",    total:46,    used:23,    pct:50, unit:"cylind.", alert:false },
  { item:"Stretchers",          total:56,    used:36,    pct:64, unit:"units",  alert:false },
  { item:"Gloves (boxes)",      total:480,   used:312,   pct:65, unit:"boxes",  alert:false },
  { item:"ICE Packs",           total:1800,  used:1480,  pct:82, unit:"packs",  alert:true  },
  { item:"Cooling Vests",       total:120,   used:108,   pct:90, unit:"vests",  alert:true  },
];

const PERSONNEL = [
  { zone:"Ramkund Ghat",    doctors:3, paramedics:8, nurses:6,  volunteers:24, total:41 },
  { zone:"Panchvati",       doctors:2, paramedics:6, nurses:4,  volunteers:18, total:30 },
  { zone:"Trimbakeshwar",   doctors:2, paramedics:5, nurses:4,  volunteers:16, total:27 },
  { zone:"Nashik Road Stn", doctors:1, paramedics:4, nurses:3,  volunteers:12, total:20 },
  { zone:"Kalaram Temple",  doctors:1, paramedics:3, nurses:2,  volunteers:10, total:16 },
  { zone:"Gangapur Camp",   doctors:1, paramedics:2, nurses:2,  volunteers:8,  total:13 },
];

const CROWD_HEALTH_CORR = [
  { zone:"Ramkund Ghat",    density:9.2, heatCases:18, dehydCases:26, risk:82 },
  { zone:"Panchvati",       density:8.1, heatCases:8,  dehydCases:18, risk:68 },
  { zone:"Trimbakeshwar",   density:7.6, heatCases:11, dehydCases:22, risk:61 },
  { zone:"Nashik Road Stn", density:6.8, heatCases:4,  dehydCases:12, risk:48 },
  { zone:"Kalaram Temple",  density:5.8, heatCases:2,  dehydCases:8,  risk:42 },
  { zone:"Gangapur Camp",   density:4.1, heatCases:1,  dehydCases:4,  risk:28 },
];

const EPIDEMIC_WATCH = [
  { disease:"Heatstroke",         zone:"Ramkund Ghat",    cases:14, r0:"N/A",  trend:"rising",   detection:"Cluster",  riskLevel:"HIGH",     action:"Medical team + cooling station deployed",        sop:["Isolate critical cases","Activate cooling zone","Dispatch AMB-03","Alert Civil Hospital ICU","Monitor temperature + density every 15 min"] },
  { disease:"Dehydration Surge",  zone:"Panchvati Entry", cases:22, r0:"N/A",  trend:"stable",   detection:"Surge",    riskLevel:"HIGH",     action:"ORS distribution — 28 active points",             sop:["Expand ORS points by 6","Increase water tanker frequency","Deploy shade tents in Entry zone","PA broadcast every 30 min"] },
  { disease:"Gastroenteritis",    zone:"Gangapur FZ",     cases:4,  r0:"1.2",  trend:"stable",   detection:"Cluster",  riskLevel:"MED",      action:"FSSAI inspection ordered — stall FZ-06",           sop:["Close stall FZ-06 for inspection","Check adjacent food zones","Sample food for lab test","Alert nearby camp nurses"] },
  { disease:"Respiratory Cluster",zone:"Trimbakeshwar A", cases:6,  r0:"1.4",  trend:"falling",  detection:"Cluster",  riskLevel:"LOW",      action:"Monitoring — no action required",                  sop:["Continue hourly surveillance","Check for environmental trigger","Report to state health if >10 cases"] },
  { disease:"Heat Exhaustion",    zone:"Nashik Road Stn", cases:8,  r0:"N/A",  trend:"rising",   detection:"Surge",    riskLevel:"MED",      action:"Rest zone + electrolytes active at Bay area",      sop:["Deploy electrolyte point at Bay 2","Shade tent at station exit","Volunteer health watch every 20 min"] },
  { disease:"Eye Infection Watch",zone:"Multiple",        cases:3,  r0:"1.1",  trend:"stable",   detection:"Watch",    riskLevel:"LOW",      action:"Monitoring — eye drop kits at all camps",          sop:["Distribute eye drop kits","Public hygiene advisory via PA","Monitor for cluster formation"] },
];

const HISTORICAL = [
  { event:"Kumbh 2015",     heatstroke:62, dehydration:148, serious:22, referrals:18, mortality:2  },
  { event:"Kumbh 2019",     heatstroke:44, dehydration:112, serious:16, referrals:12, mortality:1  },
  { event:"2027 (11 hrs)",  heatstroke:28, dehydration:74,  serious:9,  referrals:4,  mortality:0  },
];

const CASE_COMPOSITION = [
  { name:"Dehydration",   value:74 },
  { name:"Minor Injury",  value:34 },
  { name:"Heatstroke",    value:28 },
  { name:"Serious",       value:9  },
  { name:"Chronic/Other", value:24 },
];

const VULNERABLE_HEALTH = [
  { group:"Elderly (60+ yrs)",   count:142, heatRisk:"High",    protocol:"Priority triage · Cooling vest · IV preference", zone:"All zones" },
  { group:"Children (0–12 yrs)", count:86,  heatRisk:"High",    protocol:"Shade mandate · ORS push · Temp monitor",        zone:"All zones" },
  { group:"Cardiac Patients",    count:28,  heatRisk:"Critical",protocol:"Pre-identified · Ambulance proximity · ECG ready",zone:"All zones" },
  { group:"Diabetics",           count:64,  heatRisk:"High",    protocol:"Blood sugar monitoring · Dedicated rest zone",    zone:"All zones" },
  { group:"Pregnant Women",      count:34,  heatRisk:"High",    protocol:"Dedicated shaded area · Midwife on standby",      zone:"Camps only" },
];

// ── helpers ──────────────────────────────────────────────────────
// Card-tint vocabulary (critical/high/moderate/normal) -> shared status
// bucket. Border comes straight from statusColors; the translucent card
// wash has no equivalent in STATUS so it stays as a small local map.
const cardRiskBucket = r =>
  r === "critical" ? "critical" :
  r === "high"     ? "serious"  :
  r === "moderate" ? "warning"  : "good";
const CARD_WASH = { critical:"bg-red-50/30", serious:"bg-orange-50/20", warning:"bg-amber-50/10", good:"bg-emerald-50/10" };
const rBorder = r => `${STATUS[cardRiskBucket(r)].border} ${CARD_WASH[cardRiskBucket(r)]}`;

// Badge vocabulary (HIGH/MED/LOW risk labels + over/near/normal camp status).
const badgeBucket = r =>
  r === "HIGH" || r === "over" ? "critical" :
  r === "MED"  || r === "near" ? "warning"  :
  r === "LOW"                  ? "info"     : "good";
const rBadge = r => statusClasses(badgeBucket(r));

const trendIcon = t =>
  t === "rising"  ? <TrendingUp  size={11} style={{ color: statusHex("critical") }} /> :
  t === "falling" ? <TrendingDown size={11} style={{ color: statusHex("good") }}     /> :
  <Minus size={11} style={{ color: statusHex("warning") }} />;

const aqiBg = v => statusClasses(thresholdStatus(v, [{ min:100, bucket:"serious" }, { min:80, bucket:"warning" }, { min:0, bucket:"good" }]));
const tempColor = v => STATUS[thresholdStatus(v, [{ min:41, bucket:"critical" }, { min:39, bucket:"serious" }, { min:0, bucket:"warning" }])].text;

// ── component ────────────────────────────────────────────────────
export default function HealthIntelligence() {
  const [expandedDisease, setExpandedDisease] = useState(null);
  const [expandedCamp,    setExpandedCamp]    = useState(null);
  const [activeTab,       setActiveTab]       = useState("supplies");
  const t = useTranslate();

  // Computed directly from the arrays rendered below, instead of separately
  // hand-typed numbers that can drift from what the tables actually show.
  const caseCompositionTotal = CASE_COMPOSITION.reduce((s, c) => s + c.value, 0);
  const orsPointsTotal   = ORS_ZONES.reduce((s, z) => s + z.points, 0);
  const orsDistributed   = ORS_ZONES.reduce((s, z) => s + z.distributed, 0);
  const orsPacketsTotal  = SUPPLY_STOCK.find(s => s.item === "ORS Packets").total;
  const personnelTotals  = PERSONNEL.reduce((acc, p) => ({
    doctors: acc.doctors + p.doctors, paramedics: acc.paramedics + p.paramedics,
    nurses: acc.nurses + p.nurses, volunteers: acc.volunteers + p.volunteers,
  }), { doctors:0, paramedics:0, nurses:0, volunteers:0 });
  const overloadedCamps  = CAMP_DETAIL.filter(c => c.status === "over").length;
  const activeEpidemicWatch = EPIDEMIC_WATCH.filter(d => d.riskLevel === "HIGH").length;

  return (
    <div className="space-y-8">
      <SectionHeader icon="HeartPulse" title="Health & Epidemic Risk Intelligence"
        subtitle="360° heatstroke & dehydration analytics · epidemic cluster detection · medical camp load · hospital pipeline · ORS network · supply stock · environmental conditions · vulnerable pilgrim watch · AI case forecasting · Kumbh 2027 Nashik"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Heatstroke"       value={healthData.heatstroke.toString()} icon="Thermometer" color="red"    index={0} live />
        <MetricCard title="Dehydration"      value={healthData.dehydration.toString()}icon="Droplets"    color="amber"  index={1} live />
        <MetricCard title="Minor Injuries"   value={healthData.minorInjury.toString()}icon="Activity"    color="blue"   index={2} />
        <MetricCard title="Serious Cases"    value={healthData.seriousCase.toString()}icon="AlertTriangle"color="red"   index={3} live />
        <MetricCard title="Hospital Ref."    value={healthData.hospitalReferrals.toString()} icon="Building2" color="purple" index={4} />
        <MetricCard title="Ambulance Active" value={healthData.ambulanceActive.toString()}   icon="HeartPulse"color="red"   index={5} live />
        <MetricCard title="Camps Overloaded" value={overloadedCamps.toString()}      icon="AlertTriangle"color="amber"  index={6} />
        <MetricCard title="Epidemic Watch"   value={`${activeEpidemicWatch} Active`} icon="FlaskConical" color="amber"  index={7} />
      </div>

      {/* Health Command Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("HEALTH EOC — CMO NASHIK — 14:33 IST — HEAT INDEX 52°C RAMKUND")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Maha Kumbh AI Health & Epidemic Command — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {t("169 total medical cases · 9 serious · 4 hospital referrals · 5 ambulances active · Camp Alpha at 124% capacity · ORS distributed to 44,000 pilgrims · AI heatstroke forecast: peak at 16:00 — 54 cases projected (P50)")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label:"Heat Index",   val:"52°C"    },
              { label:"Air Quality",  val:"AQI 118" },
              { label:"Amb. Active",  val:"5"       },
              { label:"ORS Distrib.", val:"44K"     },
            ].map((b, i) => {
              const c = googleColor(i);
              return (
                <div key={b.label} className="rounded-xl px-3 py-2 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background: c.bgLight, border: `1px solid ${c.border}` }}>
                  <div className="text-base font-black leading-tight" style={{ color: c.text }}>{b.val}</div>
                  <div className="text-[9px] mt-0.5" style={{ color: c.text }}>{t(b.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Camp Gauges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CAMP_DETAIL.map((camp, i) => (
          <GaugeCard key={camp.id}
            title={camp.name.replace("Camp ","")}
            value={Math.round((camp.load / camp.beds) * 100)}
            index={i}
            subtitle={`${camp.load}/${camp.beds} beds`}
          />
        ))}
      </div>

      {/* Medical Trend + Forecast */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Medical Case Trend — Today (All Types)"
          subtitle="Hourly heatstroke · dehydration · injury · serious · referrals" live>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={HEALTH_TREND_EXTENDED} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="dehydGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Area type="monotone" dataKey="dehydration" fill="url(#dehydGrad)" stroke={chartColor(0)} strokeWidth={2.5} name={t("Dehydration")} />
              <Bar  dataKey="injury"      fill={chartColor(1)} radius={[3,3,0,0]} name={t("Minor Injury")} fillOpacity={0.7} />
              <Line type="monotone" dataKey="heatstroke"  stroke={chartColor(2)} strokeWidth={2.5} dot={{ r:3 }} name={t("Heatstroke")} />
              <Line type="monotone" dataKey="serious"     stroke={chartColor(3)} strokeWidth={2}   dot={{ r:3 }} name={t("Serious")} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="referral"    stroke={chartColor(4)} strokeWidth={1.5} dot={{ r:2 }} name={t("Referral")} strokeDasharray="2 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="AI Heatstroke Forecast — 12:00 to 18:00"
          subtitle="P25 / P50 / P75 projection bands · peak at 16:00" live>
          <div className="mb-2 px-1">
            <span className="badge badge-warning text-[9px]">{t("AI forecast — human CMO review required before resource allocation")}</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={FORECAST_CASES} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="p75Grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="p50Grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(1)} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={chartColor(1)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Area type="monotone" dataKey="p75" fill="url(#p75Grad)" stroke={chartColor(0)} strokeWidth={1.5} strokeDasharray="4 2" name={t("P75 (worst)")} />
              <Area type="monotone" dataKey="p50" fill="url(#p50Grad)" stroke={chartColor(1)} strokeWidth={2.5} name={t("P50 (expected)")} />
              <Area type="monotone" dataKey="p25" fill={chartColor(2)} fillOpacity={0.2} stroke={chartColor(2)} strokeWidth={1.5} strokeDasharray="4 2" name={t("P25 (best)")} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Environmental Conditions */}
      <ChartCard title="Environmental Health Conditions — Zone by Zone"
        subtitle="Temperature · heat index · humidity · UV index · AQI · misting status — updated every 15 min">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-center text-red-600">{t("Temp °C")}</th>
                <th className="text-center text-orange-600">{t("Heat Index")}</th>
                <th className="text-center text-blue-600">{t("Humidity")}</th>
                <th className="text-center text-amber-600">{t("UV Index")}</th>
                <th className="text-center text-slate-600">AQI</th>
                <th className="text-center text-emerald-600">{t("Shade Cover")}</th>
                <th className="text-center">{t("Misting")}</th>
              </tr>
            </thead>
            <tbody>
              {ENVIRONMENTAL.map((e, i) => (
                <motion.tr key={e.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-bold text-slate-800 whitespace-nowrap">
                    <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline underline-offset-2">{t(e.zone)}</Link>
                  </td>
                  <td className={`text-center font-black ${tempColor(e.temp)}`}>{e.temp}°C</td>
                  <td className="text-center">
                    <span className={`font-black px-1.5 py-0.5 rounded-md text-[10px] ${statusClasses(thresholdStatus(e.heatIndex, [{ min:50, bucket:"critical" }, { min:0, bucket:"serious" }]))}`}>
                      {e.heatIndex}°C
                    </span>
                  </td>
                  <td className="text-center font-semibold text-blue-600">{e.humidity}%</td>
                  <td className="text-center">
                    <span className={`font-black px-1 py-0.5 rounded text-[10px] ${statusClasses(thresholdStatus(e.uv, [{ min:10, bucket:"critical" }, { min:8, bucket:"serious" }, { min:0, bucket:"warning" }]))}`}>
                      {e.uv} {t(e.uv >= 8 ? "Extreme" : "Very High")}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`font-black px-1 py-0.5 rounded text-[9px] ${aqiBg(e.aqi)}`}>{e.aqi}</span>
                  </td>
                  <td className="text-center text-slate-600 font-semibold">{e.shade}</td>
                  <td className="text-center">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                      statusClasses(e.misting === "Active" ? "good" : e.misting === "Standby" ? "warning" : "neutral")
                    }`}>{t(e.misting)}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Epidemic Surveillance */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Epidemic Surveillance & Disease Cluster Board")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {EPIDEMIC_WATCH.map((d, i) => {
            const isOpen = expandedDisease === d.disease;
            return (
              <motion.div key={d.disease}
                initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className={`rounded-2xl border cursor-pointer transition-all ${rBorder(d.riskLevel === "HIGH" ? "critical" : d.riskLevel === "MED" ? "moderate" : "normal")} ${isOpen ? "shadow-card-hover" : ""}`}
                onClick={() => setExpandedDisease(isOpen ? null : d.disease)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${rBadge(d.riskLevel)}`}>{t(d.riskLevel)}</span>
                        <span className="badge badge-info text-[8px]">{t(d.detection)}</span>
                        {d.riskLevel === "HIGH" && <div className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" />}
                      </div>
                      <h4 className="text-xs font-black text-slate-900">{t(d.disease)}</h4>
                    </div>
                    {isOpen ? <ChevronUp size={12} className="text-slate-400 flex-shrink-0" /> :
                               <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />}
                  </div>
                  <div className="text-[10px] text-slate-600 mb-1.5">{t(d.zone)}</div>
                  <div className="flex items-center gap-3 text-[10px] flex-wrap">
                    <span className="flex items-center gap-1 font-bold text-slate-800">{d.cases} {t("cases")}</span>
                    <span className="flex items-center gap-1">{trendIcon(d.trend)} {t(d.trend)}</span>
                    {d.r0 !== "N/A" && <span className="text-violet-700 font-bold">R₀ {d.r0}</span>}
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/60 text-[10px] text-slate-700 font-semibold">
                    {t(d.action)}
                  </div>
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
                      className="overflow-hidden border-t border-white/60">
                      <div className="p-3 bg-white/50 rounded-b-2xl">
                        <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Response SOP")}</div>
                        <div className="space-y-1.5">
                          {d.sop.map((s, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              <span className="text-[9px] font-black text-slate-400 flex-shrink-0 mt-0.5">{idx + 1}.</span>
                              <span className="text-[10px] text-slate-700 leading-snug">{t(s)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Camp Detail Board */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Medical Camp Detail Board — Click to expand")}</h3>
        <div className="space-y-2">
          {CAMP_DETAIL.map((c, i) => {
            const isOpen = expandedCamp === c.id;
            const loadPct = Math.round((c.load / c.beds) * 100);
            const totalCases = Object.values(c.cases).reduce((s, v) => s + v, 0);
            return (
              <motion.div key={c.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`rounded-2xl border transition-all cursor-pointer ${
                  c.status === "over"   ? `${STATUS.critical.border} bg-red-50/20 shadow-card` :
                  c.status === "near"   ? `${STATUS.warning.border} bg-amber-50/10`             : STATUS.neutral.border
                } ${isOpen ? "shadow-card-hover" : ""}`}
                onClick={() => setExpandedCamp(isOpen ? null : c.id)}
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-black text-slate-900">{t(c.name)}</span>
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${rBadge(c.status)}`}>
                        {t(c.status === "over" ? "OVER CAPACITY" : c.status === "near" ? "NEAR FULL" : "OK")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin size={9} />{t(c.zone)}</span>
                      <span>{c.load}/{c.beds} {t("beds")} · {loadPct}%</span>
                      <span className="text-blue-700 font-bold">{totalCases} {t("cases today")}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden w-48">
                      <motion.div className="h-2 rounded-full" style={{ background: statusHex(thresholdStatus(loadPct, [{ min:100, bucket:"critical" }, { min:80, bucket:"warning" }, { min:0, bucket:"good" }])) }}
                        initial={{ width:0 }} animate={{ width:`${Math.min(loadPct, 100)}%` }} transition={{ duration:0.8 }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-center text-xs flex-shrink-0">
                    <div><div className="text-lg font-black text-blue-700">{c.doctors}</div><div className="text-[9px] text-slate-500">{t("Doctors")}</div></div>
                    <div><div className="text-lg font-black text-emerald-600">{c.paramedics}</div><div className="text-[9px] text-slate-500">{t("Paramedics")}</div></div>
                    <div><div className="text-lg font-black text-violet-600">{c.refToday}</div><div className="text-[9px] text-slate-500">{t("Referrals")}</div></div>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white/60 rounded-b-2xl">
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Case Breakdown")}</div>
                          <div className="space-y-1.5">
                            {[
                              { label:"Heatstroke",    val:c.cases.heatstroke, color:"text-red-600"    },
                              { label:"Dehydration",   val:c.cases.dehydration,color:"text-amber-600"  },
                              { label:"Minor Injury",  val:c.cases.injury,     color:"text-violet-600" },
                              { label:"Chronic/Other", val:c.cases.chronic,    color:"text-slate-600"  },
                            ].map(r => (
                              <div key={r.label} className="flex items-center justify-between text-[10px]">
                                <span className="text-slate-600">{t(r.label)}</span>
                                <span className={`font-black ${r.color}`}>{r.val}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Supply Status")}</div>
                          <div className="space-y-1.5">
                            {[
                              { label:"ORS Packets",    val:`${c.supplies.ors} ${t("pkts")}`     },
                              { label:"IV Fluids",      val:`${c.supplies.iv} ${t("bags")}`      },
                              { label:"O₂ Cylinders",   val:`${c.supplies.oxygen} ${t("cylnd.")}`},
                              { label:"Stretchers",     val:`${c.supplies.stretchers}`   },
                              { label:"Paracetamol",    val:`${c.supplies.paracetamol}`  },
                            ].map(s => (
                              <div key={s.label} className="flex items-center justify-between text-[10px]">
                                <span className="text-slate-600">{t(s.label)}</span>
                                <span className="font-bold text-slate-800">{s.val}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Logistics")}</div>
                          <div className="space-y-1.5 text-[10px]">
                            <div className="flex items-start gap-1.5"><Building2 size={10} className="text-blue-600 mt-0.5" /><span className="text-slate-600">{t(c.nearHospital)}</span></div>
                            <div className="flex items-start gap-1.5"><Clock size={10} className="text-slate-400 mt-0.5" /><span className="text-slate-600">{t("Last inspection")}: {c.lastInspect}</span></div>
                            <div className="flex items-start gap-1.5"><Users size={10} className="text-violet-600 mt-0.5" /><span className="text-slate-600">{c.doctors}D · {c.paramedics}P · {c.nurses}N {t("on duty")}</span></div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Zone Risk Chart + Composition */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Zone-Level Health Risk Matrix"
          subtitle="Heat · dehydration · epidemic risk by zone — grouped bar">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={healthRiskZones} layout="vertical" margin={{ left:10, right:10 }}>
              <XAxis type="number" tick={axisTick} domain={[0,100]} />
              <YAxis type="category" dataKey="zone" tick={axisTick} width={120} tickFormatter={t} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="heatRisk"        fill={chartColor(0)} radius={[0,2,2,0]} name={t("Heat Risk %")} />
              <Bar dataKey="dehydrationRisk" fill={chartColor(1)} radius={[0,2,2,0]} name={t("Dehydration %")} />
              <Bar dataKey="injuryRisk"      fill={chartColor(2)} radius={[0,2,2,0]} name={t("Injury Risk %")} />
              <Bar dataKey="epidemicRisk"    fill={chartColor(3)} radius={[0,2,2,0]} name={t("Epidemic %")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Medical Case Composition — Today">
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="55%" height={220}>
              <PieChart>
                <Pie data={CASE_COMPOSITION} dataKey="value" cx="50%" cy="50%"
                  outerRadius={90} innerRadius={50}
                  label={({ value }) => value} labelLine={false}>
                  {CASE_COMPOSITION.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {CASE_COMPOSITION.map((d, i) => (
                <div key={d.name} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-xs text-slate-700">{t(d.name)}</span>
                  </div>
                  <span className="text-sm font-black" style={{ color:chartColor(i) }}>{d.value}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100 flex justify-between text-xs">
                <span className="text-slate-500">{t("Total cases")}</span>
                <span className="font-black text-slate-800">{caseCompositionTotal}</span>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Crowd Density × Health Case Correlation — ties this page's case
          load directly back to CrowdIntelligence's live zone density
          instead of only sitting alongside it in an unreferenced dataset. */}
      <ChartCard title="Crowd Density × Health Case Correlation"
        subtitle="Zone crowd density (from Crowd Intelligence) against heatstroke + dehydration case load — higher density correlates with higher case load">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-center">{t("Crowd Density")}</th>
                <th className="text-center text-red-600">{t("Heat Cases")}</th>
                <th className="text-center text-amber-600">{t("Dehydration Cases")}</th>
                <th className="text-center">{t("Correlated Risk")}</th>
              </tr>
            </thead>
            <tbody>
              {CROWD_HEALTH_CORR.map((c, i) => (
                <motion.tr key={c.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-bold text-slate-800 whitespace-nowrap">
                    <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline underline-offset-2">{t(c.zone)}</Link>
                  </td>
                  <td className="text-center font-semibold text-slate-700">{c.density}/m²</td>
                  <td className="text-center font-black text-red-600">{c.heatCases}</td>
                  <td className="text-center font-black text-amber-600">{c.dehydCases}</td>
                  <td className="text-center">
                    <div className="flex items-center gap-1.5 justify-center">
                      <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-1.5 rounded-full" style={{ width:`${c.risk}%`, background: statusHex(thresholdStatus(c.risk, [{ min:70, bucket:"critical" }, { min:50, bucket:"warning" }, { min:0, bucket:"good" }])) }} />
                      </div>
                      <span className={`text-[10px] font-bold ${STATUS[thresholdStatus(c.risk, [{ min:70, bucket:"critical" }, { min:50, bucket:"warning" }, { min:0, bucket:"good" }])].text}`}>{c.risk}%</span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-slate-500 mt-3 pt-3 border-t border-slate-100">
          {t("Zone density figures are shared with the Crowd Intelligence page — a rising trend there is a leading indicator for medical camp load here, before case counts themselves rise.")}
        </p>
      </ChartCard>

      {/* Hospital Pipeline + ORS Network */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Hospital Referral Pipeline"
          subtitle="Capacity and ICU availability at receiving hospitals — live">
          <div className="space-y-3">
            {HOSPITALS.map((h, i) => {
              const bedPct = Math.round((h.occupied / h.beds) * 100);
              const icuPct = h.icu > 0 ? Math.round((h.icuUsed / h.icu) * 100) : 0;
              return (
                <motion.div key={h.name}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                  className={`p-3 rounded-xl border ${
                    h.status === "Overflow"  ? `${STATUS.critical.border} bg-red-50/20`    :
                    h.status === "Near Full" ? `${STATUS.warning.border} bg-amber-50/20`   : STATUS.neutral.border
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-black text-slate-900">{t(h.name)}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                          statusClasses(h.status === "Overflow" ? "critical" : h.status === "Near Full" ? "warning" : "good")
                        }`}>{t(h.status)}</span>
                      </div>
                      <div className="text-[9px] text-slate-500">{t(h.speciality)} · {h.distance}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-center text-[10px] flex-shrink-0">
                      <div>
                        <div className="font-black text-slate-800">{h.occupied}/{h.beds}</div>
                        <div className="text-slate-400">{t("Beds")} {bedPct}%</div>
                      </div>
                      {h.icu > 0 && (
                        <div>
                          <div className={`font-black ${STATUS[icuPct >= 80 ? "critical" : "warning"].text}`}>{h.icuUsed}/{h.icu}</div>
                          <div className="text-slate-400">ICU {icuPct}%</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="flex-1">
                      <div className="text-[8px] text-slate-400 mb-0.5">{t("Beds")}</div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <motion.div className="h-1.5 rounded-full" style={{ background: statusHex(thresholdStatus(bedPct, [{ min:85, bucket:"critical" }, { min:70, bucket:"warning" }, { min:0, bucket:"good" }])) }}
                          initial={{ width:0 }} animate={{ width:`${bedPct}%` }} transition={{ duration:0.8 }} />
                      </div>
                    </div>
                    {h.icu > 0 && (
                      <div className="flex-1">
                        <div className="text-[8px] text-slate-400 mb-0.5">ICU</div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <motion.div className={`h-1.5 rounded-full ${icuPct >= 80 ? "bg-red-500" : "bg-violet-500"}`}
                            initial={{ width:0 }} animate={{ width:`${icuPct}%` }} transition={{ duration:0.8 }} />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="ORS Distribution Network"
          subtitle="Oral rehydration distribution points · packets distributed · stock remaining per zone">
          <div className="space-y-3">
            {ORS_ZONES.map((z, i) => (
              <motion.div key={z.zone}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-[10px] font-semibold text-slate-700 truncate flex-1">{t(z.zone)}</span>
                  <div className="flex items-center gap-2 text-[9px] flex-shrink-0">
                    <span className="text-slate-500">{z.points} {t("pts")}</span>
                    <span className="font-black text-slate-800">{z.distributed.toLocaleString()} {t("distrib.")}</span>
                    <span className={`font-black px-1.5 py-0.5 rounded-full text-[8px] ${
                      statusClasses(z.status === "Low" ? "critical" : z.status === "OK" ? "warning" : "good")
                    }`}>{t(z.status)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2.5 rounded-full" style={{ background: statusHex(thresholdStatus(z.pct, [{ min:80, bucket:"good" }, { min:60, bucket:"warning" }, { min:0, bucket:"critical" }])) }}
                      initial={{ width:0 }} animate={{ width:`${z.pct}%` }} transition={{ duration:0.8, delay: i * 0.07 }} />
                  </div>
                  <span className="text-[9px] font-black text-slate-600 flex-shrink-0 w-12 text-right">{z.stock.toLocaleString()} {t("left")}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-base font-black text-slate-900">{orsPointsTotal}</div><div className="text-[9px] text-slate-500">{t("ORS Points")}</div></div>
            <div><div className="text-base font-black text-amber-600">{orsDistributed.toLocaleString()}</div><div className="text-[9px] text-slate-500">{t("Distributed")}</div></div>
            <div><div className="text-base font-black text-emerald-600">{orsPacketsTotal.toLocaleString()}</div><div className="text-[9px] text-slate-500">{t("Total Stock")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Supply + Personnel + Vulnerable tabs */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-black text-slate-900">{t("Medical Resources & Deployment")}</h3>
          <div className="flex gap-1 flex-wrap">
            {[
              { id:"supplies",    label:"Drug & Supply Stock"    },
              { id:"personnel",   label:"Medical Personnel"      },
              { id:"vulnerable",  label:"Vulnerable Pilgrim Watch"},
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`text-[9px] font-black px-3 py-1.5 rounded-lg transition-colors ${
                  activeTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tab.label)}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "supplies" && (
            <motion.div key="supplies" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SUPPLY_STOCK.map((s, i) => {
                  const remaining = 100 - s.pct;
                  return (
                    <motion.div key={s.item}
                      initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                      className={`p-3 rounded-xl border ${s.alert ? "border-red-200 bg-red-50/20" : "border-slate-200"}`}
                    >
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <span className="text-xs font-semibold text-slate-800">{t(s.item)}</span>
                        <div className="flex items-center gap-1.5 text-[9px] flex-shrink-0">
                          {s.alert && <AlertTriangle size={10} className="text-red-500" />}
                          <span className={`font-black ${STATUS[thresholdStatus(s.pct, [{ min:80, bucket:"critical" }, { min:60, bucket:"warning" }, { min:0, bucket:"good" }])].text}`}>{s.pct}% {t("used")}</span>
                        </div>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1">
                        <motion.div className="h-2 rounded-full" style={{ background: statusHex(thresholdStatus(s.pct, [{ min:80, bucket:"critical" }, { min:60, bucket:"warning" }, { min:0, bucket:"good" }])) }}
                          initial={{ width:0 }} animate={{ width:`${s.pct}%` }} transition={{ duration:0.8, delay: i * 0.05 }} />
                      </div>
                      <div className="flex justify-between text-[9px] text-slate-500">
                        <span>{s.used}/{s.total} {t(s.unit)}</span>
                        <span className={remaining <= 20 ? `${STATUS.critical.text} font-bold` : "text-slate-400"}>{remaining}% {t("remaining")}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <p className="text-[9px] text-amber-600 font-bold mt-3 pt-3 border-t border-slate-100">
                {t("⚠ Ice Packs (18% remaining) and Cooling Vests (10% remaining) — resupply order placed · ETA 16:30")}
              </p>
            </motion.div>
          )}

          {activeTab === "personnel" && (
            <motion.div key="personnel" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <div className="overflow-x-auto">
                <table className="gov-table w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left">{t("Zone")}</th>
                      <th className="text-center text-blue-700">{t("Doctors")}</th>
                      <th className="text-center text-emerald-700">{t("Paramedics")}</th>
                      <th className="text-center text-violet-700">{t("Nurses")}</th>
                      <th className="text-center text-amber-700">{t("Volunteers")}</th>
                      <th className="text-center">{t("Total")}</th>
                      <th className="text-center">{t("Per 10K")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PERSONNEL.map((p, i) => {
                      const crowdBase = [92140, 81200, 76400, 68100, 58200, 41800];
                      const per10k = ((p.total / crowdBase[i]) * 10000).toFixed(1);
                      return (
                        <motion.tr key={p.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                          <td className="font-bold text-slate-800 whitespace-nowrap">{t(p.zone)}</td>
                          <td className="text-center font-black text-blue-700">{p.doctors}</td>
                          <td className="text-center font-black text-emerald-600">{p.paramedics}</td>
                          <td className="text-center font-black text-violet-600">{p.nurses}</td>
                          <td className="text-center font-black text-amber-600">{p.volunteers}</td>
                          <td className="text-center font-black text-slate-900">{p.total}</td>
                          <td className="text-center">
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                              statusClasses(thresholdStatus(parseFloat(per10k), [{ min:4, bucket:"good" }, { min:2, bucket:"warning" }, { min:0, bucket:"critical" }]))
                            }`}>{per10k}</span>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-100 text-center text-xs">
                <div><div className="text-xl font-black text-blue-700">{personnelTotals.doctors}</div><div className="text-[9px] text-slate-500">{t("Total Doctors")}</div></div>
                <div><div className="text-xl font-black text-emerald-600">{personnelTotals.paramedics}</div><div className="text-[9px] text-slate-500">{t("Paramedics")}</div></div>
                <div><div className="text-xl font-black text-violet-600">{personnelTotals.nurses}</div><div className="text-[9px] text-slate-500">{t("Nurses")}</div></div>
                <div><div className="text-xl font-black text-amber-600">{personnelTotals.volunteers}</div><div className="text-[9px] text-slate-500">{t("Med Volunteers")}</div></div>
              </div>
            </motion.div>
          )}

          {activeTab === "vulnerable" && (
            <motion.div key="vulnerable" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <div className="space-y-3">
                {VULNERABLE_HEALTH.map((v, i) => (
                  <motion.div key={v.group}
                    initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                    className={`p-4 rounded-xl border ${
                      v.heatRisk === "Critical" ? `${STATUS.critical.border} bg-red-50/20` :
                      v.heatRisk === "High"     ? `${STATUS.serious.border} bg-orange-50/10` : STATUS.neutral.border
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-black text-slate-900">{t(v.group)}</span>
                          <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                            statusClasses(v.heatRisk === "Critical" ? "critical" : v.heatRisk === "High" ? "serious" : "warning")
                          }`}>{t(v.heatRisk)} {t("Risk")}</span>
                          <span className="badge badge-info text-[8px]">{t(v.zone)}</span>
                        </div>
                        <p className="text-[10px] text-slate-600 leading-snug">{t(v.protocol)}</p>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-2xl font-black text-slate-900">{v.count}</div>
                        <div className="text-[9px] text-slate-500">{t("identified pilgrims")}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Zone Risk Table + Historical */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Health Zone Risk Summary"
          subtitle="Full risk matrix per zone with overall status indicator">
          <div className="overflow-x-auto">
            <table className="gov-table w-full text-xs">
              <thead>
                <tr>
                  <th>{t("Zone")}</th>
                  <th className="text-center">{t("Heat")}</th>
                  <th className="text-center">{t("Dehydr.")}</th>
                  <th className="text-center">{t("Epidemic")}</th>
                  <th className="text-center">{t("Injury")}</th>
                  <th className="text-center">{t("Overall")}</th>
                </tr>
              </thead>
              <tbody>
                {healthRiskZones.map((z, i) => (
                  <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}
                    className="border-l-2" style={{ borderLeftColor: statusHex(cardRiskBucket(z.overallRisk)) }}>
                    <td className="font-bold text-slate-800 whitespace-nowrap">{t(z.zone)}</td>
                    <td className="text-center">
                      <div className="flex items-center gap-1 justify-center">
                        <div className="w-8 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-1.5 rounded-full bg-red-500" style={{ width:`${z.heatRisk}%` }} /></div>
                        <span className="text-[10px] font-bold text-red-600">{z.heatRisk}%</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="flex items-center gap-1 justify-center">
                        <div className="w-8 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-1.5 rounded-full bg-amber-500" style={{ width:`${z.dehydrationRisk}%` }} /></div>
                        <span className="text-[10px] font-bold text-amber-600">{z.dehydrationRisk}%</span>
                      </div>
                    </td>
                    <td className="text-center"><span className={`text-[10px] font-bold ${STATUS[thresholdStatus(z.epidemicRisk, [{ min:21, bucket:"critical" }, { min:13, bucket:"warning" }, { min:0, bucket:"good" }])].text}`}>{z.epidemicRisk}%</span></td>
                    <td className="text-center"><span className="text-[10px] font-bold text-slate-600">{z.injuryRisk}%</span></td>
                    <td className="text-center"><span className={`badge text-[9px] ${z.overallRisk === "critical" ? "badge-critical" : z.overallRisk === "high" ? "badge-high" : z.overallRisk === "moderate" ? "badge-warning" : "badge-success"}`}>{t(z.overallRisk.toUpperCase())}</span></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Historical Health Benchmark"
          subtitle="Simhastha 2027 vs Kumbh 2015 & 2019 — by 11:00 hrs mark">
          <div className="space-y-5 pt-1">
            {[
              { key:"heatstroke",  label:"Heatstroke Cases",  unit:"",    colors:["#DC2626","#F97316","#EF4444"], better:"lower" },
              { key:"dehydration", label:"Dehydration Cases", unit:"",    colors:["#92400E","#B45309","#F59E0B"], better:"lower" },
              { key:"serious",     label:"Serious Cases",     unit:"",    colors:["#5B21B6","#7C3AED","#A78BFA"], better:"lower" },
              { key:"referrals",   label:"Hospital Referrals",unit:"",    colors:["#1E3A8A","#1D4ED8","#2563EB"], better:"lower" },
              { key:"mortality",   label:"Mortality",         unit:"",    colors:["#7F1D1D","#991B1B","#B91C1C"], better:"lower" },
            ].map(m => (
              <div key={m.key}>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-1.5">{t(m.label)}</div>
                {HISTORICAL.map((h, i) => (
                  <div key={h.event} className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] text-slate-500 w-24 flex-shrink-0 truncate">{t(h.event)}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div className="h-2 rounded-full" style={{ background: m.colors[i] }}
                        initial={{ width:0 }}
                        animate={{ width: `${Math.round((h[m.key] / Math.max(...HISTORICAL.map(x => x[m.key] || 0.1))) * 80)}%` }}
                        transition={{ duration:0.9, delay: i * 0.1 }} />
                    </div>
                    <span className="text-[10px] font-black text-slate-800 w-6 text-right flex-shrink-0">{h[m.key]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="text-[9px] text-emerald-600 font-bold mt-3 pt-3 border-t border-slate-100">
            {t("✓ 2027 tracking 55% fewer heatstroke cases vs 2015 (same 11hr window) — proactive ORS + shade infrastructure")}
          </p>
        </ChartCard>
      </div>
    </div>
  );
}





